import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { APP_CONFIG } from '@config/app.config';
import { useWarehouses } from '@features/warehouses/hooks/useWarehouseOperations';
import { useToast } from '@shared/hooks/useToast';
import { MutationType } from '@domain/inventory/enums/MutationType.enum';
import { WarehouseConnectionType } from '@domain/inventory/enums/WarehouseConnectionType.enum';
import { RuleOperator } from '@domain/inventory/enums/RuleOperator.enum';
import { RuleField } from '@domain/inventory/enums/RuleField.enum';
import { Connection } from '@domain/connection/models/Connection';
import { ClientType } from '@domain/connection/models/Connector';
import ChannelDockCredentialsForm from '@features/connections/channeldock/components/ChannelDockCredentialsForm';
import { useChannelDockConnector, useChannelDockInventoryPreview } from '@features/connections/channeldock/hooks/useChannelDock';
import WarehouseConnectionSelect from '../WarehouseConnectionSelect';
import { InventoryPreview } from '@features/warehouses/components/inventory-preview/InventoryPreview';
import { container } from '@infrastructure/container';

interface ChannelDockWarehouseFormProps {
  onClose: () => void;
  onBack: () => void;
}

const ChannelDockWarehouseForm: React.FC<ChannelDockWarehouseFormProps> = ({
  onClose,
  onBack,
}) => {
  const queryClient = useQueryClient();
  const { createWarehouse, isCreating } = useWarehouses();
  const { error } = useToast();
  const { connector } = useChannelDockConnector();

  const [form, setForm] = useState({
    name: 'ChannelDock',
    location: '',
  });
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [showCreateConnection, setShowCreateConnection] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const { inventory: inventoryPreview, isLoading: isLoadingPreview } = useChannelDockInventoryPreview(selectedConnection?.id ?? '');

  const isLoading = isCreating || isSyncing;

  const handleSubmit = async () => {
    if (!selectedConnection) return;

    await createWarehouse({
      name: form.name,
      location: form.location,
      warehouseConnections: [
        {
          sourceType: WarehouseConnectionType.CHANNELDOCK,
          mutationType: MutationType.SYNC,
          rules: [
            {
              operator: RuleOperator.EQUALS,
              field: RuleField.CONNECTION_ID,
              value: selectedConnection.id,
            },
          ],
        },
      ],
    });

    try {
      setIsSyncing(true);
      const sync = await container.channeldockConnectorRepository.syncFull(selectedConnection.id);
      if (sync.status === 'error') {
        error(sync.error.message ?? 'Warehouse created, but the first inventory sync did not start');
      }
    } finally {
      setIsSyncing(false);
    }

    onClose();
  };

  const handleNewConnection = (connection: Connection) => {
    setShowCreateConnection(false);
    queryClient.invalidateQueries({ queryKey: ['connections'] });
    setSelectedConnection(connection);
  };

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center">
            <IconButton onClick={onBack} color="inherit">
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" component="div">
              Sync with ChannelDock
            </Typography>
          </Stack>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1 }}>
        <Stack gap={3} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ width: 100, height: 60, borderRadius: 1, border: '1px solid', borderColor: APP_CONFIG.ui.theme.colors.border, p: 2 }}>
              <img src={connector?.image} alt="ChannelDock" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
            </Box>
            <Box>
              <Typography variant="h6" color="text.primary">
                Sync your inventory with ChannelDock
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Your ChannelDock inventory will be synced with your warehouse inventory every hour.
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack gap={2}>
            <Typography variant="body2" color="text.secondary">
              Warehouse details
            </Typography>
            <Stack gap={2} direction="row">
              <TextField
                label="Warehouse name"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label="Location"
                value={form.location}
                onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                fullWidth
                size="small"
                placeholder="e.g., Amsterdam, Netherlands"
              />
            </Stack>
          </Stack>

          <WarehouseConnectionSelect
            connectorType={ClientType.CHANNELDOCK}
            selectedConnection={selectedConnection}
            onSelect={setSelectedConnection}
            onCreateConnection={() => setShowCreateConnection(true)}
            connectorName="ChannelDock"
          />

          {selectedConnection && (
            <InventoryPreview
              inventory={inventoryPreview}
              isLoading={isLoadingPreview}
              emptyMessage="No inventory to show for this connection"
              title="Preview of warehouse inventory"
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedConnection || isLoading}
        >
          {isLoading ? <CircularProgress size={20} /> : 'Create warehouse'}
        </Button>
      </DialogActions>

      <Dialog
        open={showCreateConnection}
        onClose={() => setShowCreateConnection(false)}
        maxWidth="sm"
        fullWidth
      >
        <ChannelDockCredentialsForm
          onNewConnection={handleNewConnection}
          onClose={() => setShowCreateConnection(false)}
        />
      </Dialog>
    </>
  );
};

export default ChannelDockWarehouseForm;
